import {
  Box,
  Button,
  Checkbox,
  LinearProgress,
  Radio,
  Textarea,
  Typography,
} from "@mui/joy";
import { SxProps } from "@mui/joy/styles/types";
import {
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Paper } from "../atoms/paper";
import { ConfettiCenterContext } from "../context/confettiCenter/confettiCenter";
import { CallTheme, labelOnFill } from "../entities/callDesign";
import {
  PollQuestionView,
  PollView,
  pollDisplayTitle,
  pollIsAnswered,
} from "../entities/missionMap/missionMap";
import { useAnswerMutation } from "../redux/missionMap";
import { HAIRLINE, INK, MUTED } from "../templates/modernInvite";

// One question's local answer state: the chosen option ids and any free text.
type QuestionAnswer = { optionIDs: number[]; text: string };

/**
 * One section of the poll. On a legacy page each is its own floating card over
 * the org's colored ground; on a modern call the page IS white paper, so the
 * same sections become hairline-ruled blocks — the treatment every other block
 * of a modern call wears (templates/modernInvite).
 *
 * @returns {ReactElement} The section wrapper.
 */
function PollBlock({
  modern,
  first,
  children,
  sx,
}: {
  modern: boolean;
  /** Skips the top rule — the first block sits right under the masthead. */
  first?: boolean;
  children: ReactNode;
  sx?: SxProps;
}) {
  if (!modern)
    return (
      <Paper sx={{ alignItems: "start", textAlign: "start", ...sx }}>
        {children}
      </Paper>
    );

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "start",
        textAlign: "start",
        gap: 1,
        pt: first ? 0 : 2.5,
        pb: 1,
        borderTop: first ? "none" : HAIRLINE,
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

/**
 * PollCard renders one poll and its answer form. It is reused both by the
 * standalone poll page (PollInvite) and inline inside a project/season invite,
 * where several polls can appear. A poll holds an ordered list of questions;
 * the musician answers them together and a single Submit builds ONE pollAnswer
 * inspector whose message is {"answers":[{questionID,optionIDs,text}, ...]}.
 * Answering targets the poll's own token, so the invite's own accept/decline
 * flow is untouched.
 *
 * Pass `theme` to dress the poll as a modern call (white paper, org accent,
 * hairline sections). Without it — the poll pill's overlay, which floats the
 * card over a dark scrim — the card look is the right one and nothing changes.
 *
 * @returns {ReactElement} The poll card.
 */
export function PollCard({
  poll,
  theme,
}: {
  poll: PollView;
  theme?: CallTheme;
}) {
  const [answer] = useAnswerMutation();
  const confettiCenter = useContext(ConfettiCenterContext);
  const modern = theme?.modern === true;

  const questions = useMemo(() => poll.questions ?? [], [poll.questions]);

  // Per-question local answer state, keyed by questionID.
  const [answers, setAnswers] = useState<Record<number, QuestionAnswer>>({});
  const [editing, setEditing] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Pre-fill each question from its selectedOptionIDs/text — but only ONCE per
  // poll. The mission map refetches on an interval, handing us a fresh `poll`
  // object (new `questions` array reference) every few seconds; without this
  // guard the effect would re-run on each refetch and wipe the musician's
  // in-progress selection. Re-prefill only when the poll identity changes.
  const prefilledToken = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (prefilledToken.current === poll.publicToken) return;
    prefilledToken.current = poll.publicToken;
    const next: Record<number, QuestionAnswer> = {};
    questions.forEach((q) => {
      next[q.questionID] = {
        optionIDs: q.selectedOptionIDs ?? [],
        text: q.text ?? "",
      };
    });
    setAnswers(next);
  }, [questions, poll.publicToken]);

  const answered = pollIsAnswered(poll);
  const closed = poll.status === "closed";
  const eyebrow = `Poll${poll.scope ? ` · ${poll.scope}` : ""}`;

  const hasChoices = (q: PollQuestionView) =>
    q.kind === "choices" || q.kind === "both";
  const hasText = (q: PollQuestionView) =>
    q.kind === "text" || q.kind === "both";

  // A choices question requires at least one selection (or minChoices if higher).
  const minFor = (q: PollQuestionView) =>
    Math.max(hasChoices(q) ? 1 : 0, q.minChoices ?? 0);

  const toggleOption = (q: PollQuestionView, id: number) => {
    setAnswers((prev) => {
      const current = prev[q.questionID] ?? { optionIDs: [], text: "" };
      let optionIDs: number[];
      if (q.multiSelect) {
        optionIDs = current.optionIDs.includes(id)
          ? current.optionIDs.filter((x) => x !== id)
          : [...current.optionIDs, id];
      } else {
        optionIDs = [id];
      }
      return { ...prev, [q.questionID]: { ...current, optionIDs } };
    });
  };

  const setText = (q: PollQuestionView, text: string) => {
    setAnswers((prev) => {
      const current = prev[q.questionID] ?? { optionIDs: [], text: "" };
      return { ...prev, [q.questionID]: { ...current, text } };
    });
  };

  // Every question must be validly answered before the poll can be submitted.
  const questionValid = (q: PollQuestionView) => {
    const a = answers[q.questionID] ?? { optionIDs: [], text: "" };
    if (hasChoices(q)) {
      const min = Math.max(1, minFor(q));
      if (a.optionIDs.length < min) return false;
      const max = q.maxChoices ?? 0;
      if (q.multiSelect && max > 0 && a.optionIDs.length > max) return false;
    }
    if (hasText(q) && !hasChoices(q) && a.text.trim().length === 0) return false;
    if (!hasChoices(q) && !hasText(q)) return false;
    return true;
  };

  const valid = useMemo(() => {
    if (!questions.length) return false;
    return questions.every((q) => questionValid(q));
  }, [questions, answers]);

  const submit = async () => {
    if (!poll.publicToken || !valid) return;
    setSubmitting(true);
    try {
      // Build ONE pollAnswer inspector covering every question the musician
      // answered: {"answers":[{questionID,optionIDs,text}, ...]}.
      const payload = questions.map((q) => {
        const a = answers[q.questionID] ?? { optionIDs: [], text: "" };
        return {
          questionID: q.questionID,
          optionIDs: a.optionIDs,
          text: a.text.trim(),
        };
      });
      await answer({
        [poll.publicToken]: {
          mercuryInspectors: [
            {
              kind: "pollAnswer",
              title: "pollAnswer",
              message: JSON.stringify({ answers: payload }),
            },
          ],
        },
      }).unwrap();
      setEditing(false);
      confettiCenter.show();
    } catch (e) {
      // Keep the form usable on a failed submit rather than leaving an
      // unhandled rejection; the musician can retry.
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 1 }}>
      {/* Poll header */}
      <PollBlock modern={modern} first>
        {/* The modern masthead already names the kind, so the eyebrow there is
            the subject alone — and nothing at all when there is no scope. */}
        {!modern || poll.scope ? (
          <Typography
            level="body-xs"
            sx={
              modern
                ? {
                    textTransform: "uppercase",
                    letterSpacing: "0.1em",
                    fontSize: 11,
                    color: MUTED,
                  }
                : { textTransform: "uppercase", letterSpacing: 1, opacity: 0.6 }
            }
          >
            {modern ? poll.scope : eyebrow}
          </Typography>
        ) : (
          []
        )}
        {poll.greeting ? (
          <div
            className="ql-editor"
            style={{ padding: 0 }}
            dangerouslySetInnerHTML={{ __html: poll.greeting }}
          />
        ) : (
          []
        )}
        <Typography
          level="h4"
          sx={
            modern
              ? {
                  mt: 0.5,
                  color: INK,
                  fontWeight: 700,
                  lineHeight: 1.25,
                  fontFamily: theme?.fontStack,
                }
              : { mt: 0.5 }
          }
        >
          {pollDisplayTitle(poll)}
        </Typography>
      </PollBlock>

      {/* Closed */}
      {closed ? (
        <PollBlock modern={modern}>
          <Typography level="title-md">This poll is closed.</Typography>
          <Typography level="body-sm">
            {answered
              ? "Thanks — your answer was recorded."
              : "It is no longer accepting answers."}
          </Typography>
        </PollBlock>
      ) : (
        []
      )}

      {/* Answered confirmation */}
      {answered && !editing && !closed ? (
        <PollBlock modern={modern}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <i
              className="fa-solid fa-circle-check"
              style={{ color: "#4caf50", fontSize: 20 }}
            />
            <Typography level="title-md">Answer recorded</Typography>
          </Box>
          <Typography level="body-sm">
            You can change your answer while the poll is open.
          </Typography>
          <Button
            variant="soft"
            size="sm"
            sx={{ mt: 1 }}
            onClick={() => setEditing(true)}
          >
            Change my answer
          </Button>
        </PollBlock>
      ) : (
        []
      )}

      {/* Form — one block per question, then a single Submit */}
      {!closed && !(answered && !editing) ? (
        <PollBlock modern={modern}>
          <Box
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              gap: 3,
            }}
          >
            {questions.map((q) => {
              const a = answers[q.questionID] ?? { optionIDs: [], text: "" };
              const options = q.options ?? [];
              const min = minFor(q);
              const max = q.maxChoices ?? 0;
              return (
                <Box
                  key={q.questionID}
                  sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 1 }}
                >
                  <Typography level="title-md">{q.prompt}</Typography>

                  {hasChoices(q) ? (
                    <Box sx={{ width: "100%" }}>
                      {q.multiSelect ? (
                        <Box
                          sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                        >
                          {options.map((option) => (
                            <Checkbox
                              key={option.id}
                              label={option.label}
                              checked={a.optionIDs.includes(option.id)}
                              onChange={() => toggleOption(q, option.id)}
                            />
                          ))}
                          <Typography level="body-xs" sx={{ opacity: 0.6 }}>
                            {min > 1
                              ? `Select at least ${min}${
                                  max > 0 ? `, up to ${max}` : ""
                                }.`
                              : max > 0
                              ? `Select up to ${max}.`
                              : "Select all that apply."}
                          </Typography>
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                          }}
                        >
                          {/* Individually-controlled radios (checked+onChange),
                              mirroring the checkbox branch. A controlled Joy
                              RadioGroup whose value flips null→number with
                              number-typed options does not reflect the
                              selection — so control each radio directly. */}
                          {options.map((option) => (
                            <Radio
                              key={option.id}
                              name={`poll-q-${q.questionID}`}
                              label={option.label}
                              checked={a.optionIDs[0] === option.id}
                              onChange={() => toggleOption(q, option.id)}
                            />
                          ))}
                        </Box>
                      )}
                    </Box>
                  ) : (
                    []
                  )}

                  {hasText(q) ? (
                    <Box sx={{ width: "100%", mt: hasChoices(q) ? 1 : 0 }}>
                      <Textarea
                        minRows={3}
                        placeholder={
                          q.kind === "both"
                            ? "Add a comment (optional)"
                            : "Type your answer…"
                        }
                        value={a.text}
                        onChange={(e) => setText(q, e.target.value)}
                      />
                    </Box>
                  ) : (
                    []
                  )}

                  {/* Per-question public results — the backend only sends these
                      once the musician answered (or the poll closed), to avoid
                      biasing the response. */}
                  {poll.resultsPublic && q.results && q.results.length ? (
                    <QuestionResults results={q.results} />
                  ) : (
                    []
                  )}
                </Box>
              );
            })}
          </Box>

          <Button
            fullWidth
            loading={submitting}
            disabled={!valid}
            onClick={submit}
            sx={
              // A modern call paints exactly one filled color — the response
              // button — in the org's accent (entities/callDesign).
              modern && theme
                ? {
                    mt: 2,
                    background: theme.accent,
                    color: labelOnFill(theme.accent),
                    fontFamily: theme.fontStack,
                    "&:hover": { background: theme.accent, opacity: 0.9 },
                  }
                : { mt: 2 }
            }
          >
            {answered ? "Update my answer" : "Submit"}
          </Button>
        </PollBlock>
      ) : (
        []
      )}

      {/* When the poll is closed or answered without editing, still surface the
          per-question results so the musician can see the outcome. */}
      {(closed || (answered && !editing)) && poll.resultsPublic ? (
        <PollBlock modern={modern}>
          <Typography level="title-sm">Results</Typography>
          <Box
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              gap: 2,
              mt: 0.5,
            }}
          >
            {questions.map((q) =>
              q.results && q.results.length ? (
                <Box key={q.questionID} sx={{ width: "100%" }}>
                  <Typography level="body-sm" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {q.prompt}
                  </Typography>
                  <QuestionResults results={q.results} />
                </Box>
              ) : (
                []
              )
            )}
          </Box>
        </PollBlock>
      ) : (
        []
      )}
    </Box>
  );
}

/**
 * QuestionResults renders per-option bars for one question's public results.
 * Bars are scaled to the highest count; the percentage is of the total votes
 * cast on that question.
 *
 * @returns {ReactElement} The results bars.
 */
function QuestionResults({
  results,
}: {
  results: { id: number; label: string; count: number }[];
}) {
  const maxCount = Math.max(1, ...results.map((r) => r.count));
  const total = results.reduce((sum, r) => sum + r.count, 0);
  return (
    <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 1 }}>
      {results.map((r) => {
        const pct = total > 0 ? Math.round((r.count / total) * 100) : 0;
        return (
          <Box key={r.id}>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography level="body-sm">{r.label}</Typography>
              <Typography level="body-sm" sx={{ fontWeight: 600 }}>
                {r.count}
                <Typography
                  level="body-xs"
                  sx={{ color: "text.tertiary", ml: 0.5 }}
                >
                  {pct}%
                </Typography>
              </Typography>
            </Box>
            <LinearProgress
              determinate
              value={(r.count / maxCount) * 100}
              sx={{ "--LinearProgress-thickness": "8px" }}
            />
          </Box>
        );
      })}
    </Box>
  );
}
